import { useEffect, useRef, useState } from "react";
import { api, type SurpriseTwist } from "./api";

// "Surprise me" (feature surpriseMe): three twists the student didn't think of. Picking one puts
// its message in the message box; the student still presses Send. A small button in the helper's
// header; the twists open in a panel just under it, over the chat.

type State = { status: "idle" } | { status: "thinking" } | { status: "ready"; twists: SurpriseTwist[] } | { status: "error"; message: string };

export function SurpriseMe({ projectId, disabled, onPick }: { projectId: string; disabled?: boolean; onPick: (message: string) => void }) {
  const [state, setState] = useState<State>({ status: "idle" });
  // Each press gets a number; an answer for an older press (or another project) is ignored.
  const run = useRef(0);
  useEffect(() => {
    run.current++;
    setState({ status: "idle" });
  }, [projectId]);

  const press = async () => {
    const mine = ++run.current;
    setState({ status: "thinking" });
    try {
      const { twists } = await api.surprise(projectId);
      if (mine === run.current) setState({ status: "ready", twists });
    } catch (err) {
      if (mine === run.current) setState({ status: "error", message: err instanceof Error ? err.message : "Couldn't think of twists. Try again." });
    }
  };

  const open = state.status !== "idle";
  return (
    <div className="surprise">
      <button
        type="button"
        className={`surprise-btn${open ? " on" : ""}`}
        disabled={disabled || state.status === "thinking"}
        aria-label="Surprise me"
        aria-expanded={open}
        title="Surprise me: three twists you didn't think of"
        onClick={press}
      >
        <Sparkle />
        <span className="surprise-label" aria-hidden="true">
          Surprise
        </span>
      </button>
      {open && (
        <div className="surprise-pop" role="region" aria-label="Surprise me">
          {state.status === "thinking" && (
            <p className="muted small" role="status">
              Thinking of twists…
            </p>
          )}
          {state.status === "error" && (
            <p className="muted small" role="alert">
              {state.message}
            </p>
          )}
          {state.status === "ready" && (
            <>
              <p className="muted small">Pick one to put it in the message box. You still press Send.</p>
              <div className="twists" role="group" aria-label="Three twists">
                {state.twists.map((t) => (
                  <button
                    key={t.title}
                    type="button"
                    className="twist"
                    onClick={() => {
                      run.current++;
                      setState({ status: "idle" });
                      onPick(t.message);
                    }}
                  >
                    <b>{t.title}</b>
                    <span className="small">{t.pitch}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <button
            type="button"
            className="linklike small"
            onClick={() => {
              run.current++;
              setState({ status: "idle" });
            }}
          >
            Never mind
          </button>
        </div>
      )}
    </div>
  );
}

/** A pixel sparkle. */
function Sparkle() {
  return (
    <svg width={16} height={16} viewBox="0 0 8 8" shapeRendering="crispEdges" aria-hidden="true">
      <path fill="currentColor" d="M3 0h2v2H3zM3 6h2v2H3zM0 3h2v2H0zM6 3h2v2H6zM2 2h4v4H2z" />
      <path fill="var(--gold)" d="M3 3h2v2H3z" />
    </svg>
  );
}
